import * as Sentry from "@sentry/vue";

// Every wording Vite and the browsers use when a hashed chunk from a previous deploy is gone. Also
// fed to Sentry's ignoreErrors: the reload below is the handling, so the error itself is not news.
export const STALE_ASSET_MESSAGES = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "Unable to preload CSS for",
  "CSS preload failed",
  "is not a valid JavaScript MIME type",
];

export const isStaleAssetError = (error: unknown): error is Error =>
  error instanceof Error && STALE_ASSET_MESSAGES.some((message) => error.message.includes(message));

const RELOAD_AT_KEY = "stale_asset_reload_at";
// A reload that lands back here this quickly did not fetch a newer build. Stop and report instead.
const LOOP_WINDOW_MS = 10_000;

type Deps = {
  storage: Pick<Storage, "getItem" | "setItem">;
  reload: () => void;
  now: () => number;
  report: (error: Error) => void;
};

// Returns true while a reload is under way, false when the error should keep propagating.
export const createStaleAssetRecovery = ({ storage, reload, now, report }: Deps) => {
  let reloading = false;

  return (error: unknown): boolean => {
    if (!isStaleAssetError(error)) return false;
    if (reloading) return true;

    try {
      const lastAttempt = Number(storage.getItem(RELOAD_AT_KEY)) || 0;
      if (now() - lastAttempt < LOOP_WINDOW_MS) {
        report(new Error("Reloading did not recover from a stale asset", { cause: error }));
        return false;
      }
      storage.setItem(RELOAD_AT_KEY, String(now()));
    } catch {
      // without session storage there is no loop guard, and a reload loop is worse than a dead click
      report(new Error("Could not guard a stale asset reload", { cause: error }));
      return false;
    }

    reloading = true;
    reload();
    return true;
  };
};

let recovery: ReturnType<typeof createStaleAssetRecovery> | undefined;

export const recoverFromStaleAsset = (error: unknown): boolean => {
  recovery ??= createStaleAssetRecovery({
    storage: {
      getItem: (key) => window.sessionStorage.getItem(key),
      setItem: (key, value) => window.sessionStorage.setItem(key, value),
    },
    reload: () => window.location.reload(),
    now: Date.now,
    report: (error) => Sentry.captureException(error),
  });
  return recovery(error);
};
